import { Head, Link } from "@inertiajs/react";
import { Badge, Box, Button, Flex, Section, Text, Theme } from "@radix-ui/themes";
import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import { PanelRightClose, X } from "lucide-react";
import { ManagerEventsIndexEvent, ManagerEventsIndexProps } from "@/types/manager_events";
import EventsTable from "@/shared/tables/manager/EventsTable";
import ManagerPageHeader from "@/shared/manager/ManagerPageHeader";
import EventDetailPanel from "@/shared/manager/EventDetailPanel";
import ExpandableCards from "@/shared/manager/ExpandableCards";
import useIsMobile from "@/shared/hooks/useIsMobile";
import { badgeColors } from "@/shared/constants/constants";

export default function ManagerEventsIndex({ events, paths }: ManagerEventsIndexProps) {
  const isMobile = useIsMobile();
  const [selectedRow, setSelectedRow] = useState<ManagerEventsIndexEvent | null>(
    null,
  );

  useEffect(() => {
    if (selectedRow) {
      const updated = events.find((e) => e.id === selectedRow.id);
      if (updated) setSelectedRow(updated);
    }
  }, [events]);

  if (isMobile) {
    return (
      <div className="flex">
        <Head title="My Events" />
        <div className="grow min-w-0">
          <ManagerPageHeader title="Events">
            <Button asChild>
              <Link href={paths.new}>New Event</Link>
            </Button>
          </ManagerPageHeader>
          <Section size="1">
            <Box px="3">
              <ExpandableCards
                items={events}
                getKey={(event) => event.id}
                emptyMessage="You don't have any events yet."
                renderSummary={(event) => (
                  <Flex direction="column" gap="1">
                    <p className="truncate text-[15px] font-bold leading-tight">
                      {event.name}
                    </p>
                    <span className="truncate text-sm text-gray11">
                      {event.track_name} · {event.date}
                    </span>
                    <Flex gap="2" mt="1" wrap="wrap">
                      {/* Same three states the table's Publish At column shows. */}
                      {event.is_published ? (
                        <Badge variant="soft" color={badgeColors["published"]}>
                          Published
                        </Badge>
                      ) : event.publish_at === null ? (
                        <Badge variant="soft" color={badgeColors["draft"]}>
                          Draft
                        </Badge>
                      ) : (
                        <Badge variant="soft" color="gray">
                          Publishes {event.publish_at}
                        </Badge>
                      )}
                      {event.pending_count > 0 && (
                        <Badge variant="soft" color="indigo">
                          {event.pending_count} pending
                        </Badge>
                      )}
                    </Flex>
                  </Flex>
                )}
                renderDetail={(event) => <EventDetailPanel event={event} compact />}
              />
            </Box>
          </Section>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Head title="My Events" />
      <div className="grow min-w-0">
        <ManagerPageHeader title="Events">
          <Button asChild>
            <Link href={paths.new}>New Event</Link>
          </Button>
        </ManagerPageHeader>
        <Dialog.Root
          open={selectedRow !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedRow(null);
          }}
        >
          <Section size="1">
            <Box style={{ overflowX: "auto" }}>
              <EventsTable events={events} setSelectedRow={setSelectedRow} />
            </Box>
          </Section>

          <Dialog.Portal>
            <Theme>
              <Dialog.Overlay
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "var(--color-overlay)",
                }}
              />
              <Dialog.Content
                style={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  width: "min(440px, 100vw)",
                  height: "100vh",
                  background: "var(--color-panel-solid)",
                  padding: "var(--space-5)",
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-4)",
                }}
              >
                <Flex justify="between" align="center">
                  <Dialog.Title asChild>
                    <Text size="5" weight="bold">
                      Event Detail
                    </Text>
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <Button variant="ghost" size="1">
                      <X size="16px" />
                    </Button>
                  </Dialog.Close>
                </Flex>
                {selectedRow && <EventDetailPanel event={selectedRow} />}
                <Dialog.Close asChild>
                  <Button
                    variant="soft"
                    size="3"
                    className="sm:hidden"
                    style={{
                      position: "sticky",
                      bottom: 0,
                      alignSelf: "flex-end",
                      marginTop: "auto",
                      backgroundColor: "var(--color-panel-solid)",
                    }}
                  >
                    <PanelRightClose size="16px" />
                    Close
                  </Button>
                </Dialog.Close>
              </Dialog.Content>
            </Theme>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </div>
  );
}
